<template>
  <DataPage
    ref="documentsDataPage"
    :fetch-callback="fetchDocuments"
    :model="DocumentModel"
    list
    :selectable="isSelectable"
    :row-selected="handleRowSelect"
  >
    <template #pageHeader>
      <div>
        <EntityShowHeader :entity="entity" />
        <DocumentResourceNav :resource="'entities'" />
      </div>
    </template>

    <template #tableHeaderActions>
      <b-button
        v-can:entities.documents.upload
        variant="primary"
        size="sm"
        @click="openUploadModal"
      >
        <Icon icon="cloud-upload" size="14" class="mr-2" />
        {{ __('buttons.upload') }}
      </b-button>
      <b-button
        variant="outline-primary"
        size="sm"
        class="ml-2"
        @click="handleNewFolderClick"
      >
        <Icon class="mr-2" icon="folder" size="14" />
        {{ __('documents.button-new-folder') }}
      </b-button>
      <b-button
        v-if="hasSelectedRows"
        v-can:documents.update
        class="ml-2"
        variant="outline-warning"
        size="sm"
        @click="moveDocumentInBulk"
      >
        <Icon icon="arrow-right" class="mr-2" size="14" />
        {{ __('buttons.move') }}
      </b-button>
    </template>

    <template #iconBlock="{ item }">
      <img :src="getFileTypeIcon(item)" :alt="item.name" width="48" />
    </template>

    <template #label="{ item }">
      <RouterLink
        v-if="item.isFolder"
        v-can:entities.documents.view
        :to="{
          name: 'entities.documents.list.folder',
          params: { folderId: item.id },
        }"
      >
        {{ item.name }}
      </RouterLink>
      <a v-else href="#" @click.prevent="download(item.id)">
        {{ item.name }}
      </a>
    </template>

    <template #badge="{ item }">
      <Badge v-if="item.fileType" variant="soft-secondary" class="ml-3">
        {{ item.fileType }}
      </Badge>
    </template>

    <template #description="{ item }">
      <DocumentRowSummary v-if="item" :document="item" />
    </template>

    <template #actionButtons="{ item }">
      <Badge v-if="item.type" variant="soft-info" class="ml-3">
        {{ item.type.name }}
      </Badge>

      <ActionPopover
        v-if="item.isFolder"
        :id="item.id"
        v-can-any="['documents.update', 'documents.destroy']"
      >
        <b-button
          v-can:documents.update
          class="dropdown-item"
          size="sm"
          @click="handleEditFolderClick(item)"
        >
          <Icon icon="edit" class="mr-2" size="14" />
          {{ __('buttons.edit') }}
        </b-button>
        <b-button
          v-can:documents.destroy
          class="dropdown-item"
          size="sm"
          @click="removeFolder(item.id)"
        >
          <Icon icon="trash" class="mr-2" size="14" />
          {{ __('buttons.delete') }}
        </b-button>
      </ActionPopover>

      <ActionPopover
        v-else
        :id="item.id"
        v-can-any="[
          'entities.documents.download',
          'entities.documents.destroy',
        ]"
      >
        <b-button
          v-can:entities.documents.download
          class="dropdown-item"
          size="sm"
          @click="download(item.id)"
        >
          <Icon icon="cloud-download" class="mr-2" size="14" />
          {{ __('buttons.download') }}
        </b-button>

        <b-button
          v-can:entities.documents.destroy
          class="dropdown-item"
          size="sm"
          @click="remove(item.id)"
        >
          <Icon icon="trash" class="mr-2" size="14" />
          {{ __('buttons.delete') }}
        </b-button>

        <b-button
          v-can:documents.update
          class="dropdown-item"
          size="sm"
          @click="moveDocument(item)"
        >
          <Icon icon="arrow-right" class="mr-2" size="14" />
          {{ __('buttons.move') }}
        </b-button>
      </ActionPopover>
    </template>
  </DataPage>
</template>

<script>
import FileManager from '@/General/FileManager.js'
import LazyLoadComponent from '@/General/LazyLoadComponent.js'
import DataPage from '@/General/Tables/DataPage.vue'
import DocumentModel from '@/Modules/Document/DocumentModel.js'
import UploadModal from '@/Modules/Document/Components/UploadModal.vue'
import HandlesFileUploadMixin, {
  documentServices,
} from '@/Modules/Document/Mixins/HandlesFileUploadMixin.js'
import EntityDocumentService from '@/Modules/Entity/EntityDocument/EntityDocumentService.js'
import EntityService from '@/Modules/Entity/EntityService.js'
import EntityShowHeader from '@/Modules/Entity/Components/EntityShowHeader.vue'
import EditDocumentFolderModal from '@/Modules/Entity/EntityDocument/Components/EditDocumentFolderModal.vue'
import MoveDocumentModal from '@/Modules/Entity/EntityDocument/Components/MoveDocumentModal.vue'
import DocumentResourceNav from '@/Modules/Document/Components/OneDrive/DocumentResourceNav.vue'
import DocumentRowSummary from '@/Modules/Document/Components/DocumentRowSummary.vue'

export default {
  name: 'List',

  components: {
    EntityShowHeader,
    DataPage,
    DocumentResourceNav,
    DocumentRowSummary,
  },

  extends: LazyLoadComponent((to, callback) => {
    EntityService.show(to.params.entityId).then((entity) => {
      callback(function () {
        this.entity = entity
      })
    })
  }),

  mixins: [HandlesFileUploadMixin],

  data() {
    return {
      DocumentModel: DocumentModel,
      entity: {},
      selectedDocuments: [],
    }
  },

  computed: {
    hasSelectedRows: function () {
      return !!this.selectedDocuments.length
    },
  },

  created() {
    this.$watch('$route.params', this.handleRouteChange)
  },

  methods: {
    isSelectable(item) {
      return !item?.isFolder
    },

    handleRowSelect(rows) {
      this.selectedDocuments = [...rows]
    },

    handleRouteChange() {
      this.$refs.documentsDataPage.refreshTable()
    },

    fetchDocuments(parameters) {
      const { entityId, folderId } = this.$route.params
      if (folderId) {
        return EntityDocumentService.listNested(entityId, folderId, parameters)
      }
      return EntityDocumentService.list(entityId, parameters)
    },

    getFileTypeIcon(item) {
      if (item.isFolder) {
        return FileManager.getFolderTypeIcon()
      }
      return FileManager.getFileTypeIcon(item.fileName)
    },

    download(id) {
      EntityDocumentService.show(this.$route.params.entityId, id)
    },

    remove(id) {
      this.$showConfirmationModal(
        this.__(`documents.confirmation-delete`),
        () => {
          EntityDocumentService.delete(this.$route.params.entityId, id).then(
            () => {
              this.$successToast(this.__(`documents.delete-success`))
              this.$refs.documentsDataPage.refreshTable()
            }
          )
        }
      )
    },

    removeFolder(id) {
      this.$showConfirmationModal(
        this.__(`documents.folder-confirmation-delete`),
        () => {
          EntityDocumentService.deleteFolder(this.$route.params.entityId, id)
            .then(() => {
              this.$successToast(this.__(`documents.folder-delete-success`))
              this.$refs.documentsDataPage.refreshTable()
            })
            .catch((error) => {
              this.$errorToast(error.response.data.message)
            })
        }
      )
    },

    openUploadModal() {
      EntityDocumentService.listFolders(this.$route.params.entityId, {}).then(
        (response) => {
          this.showModal({
            component: UploadModal,
            label: this.__(`documents.upload`),
            icon: 'cloud-upload',
            class: 'modal-md',
            meta: {
              documentFolderId: this.$route.params.folderId,
              documentFolders: response?.data?.data,
              uploadCallback: (formData, multiple = false) =>
                this.handleFileUpload(
                  formData,
                  this.$route.params.entityId,
                  multiple,
                  documentServices.entity
                ),
            },
            onSuccess: this.$refs.documentsDataPage.refreshTable,
          })
        }
      )
    },

    handleNewFolderClick() {
      this.showModal({
        component: EditDocumentFolderModal,
        label: this.__(`documents.create-folder`),
        icon: 'folder',
        class: 'modal-sm',
        meta: {
          parentFolderId: this.$route.params.folderId,
          entityId: this.$route.params.entityId,
        },
        onSuccess: () => {
          this.$refs.documentsDataPage.refreshTable()
        },
      })
    },

    handleEditFolderClick(item) {
      this.showModal({
        component: EditDocumentFolderModal,
        label: this.__(`documents.edit-folder`),
        icon: 'folder',
        class: 'modal-sm',
        meta: {
          parentFolderId: this.$route.params.folderId,
          entityId: this.$route.params.entityId,
          documentFolder: {
            id: item.id,
            name: item.name,
          },
        },
        onSuccess: () => {
          this.$refs.documentsDataPage.refreshTable()
        },
      })
    },

    moveDocument(item) {
      EntityDocumentService.listFolders(this.$route.params.entityId, {}).then(
        (response) => {
          this.showModal({
            component: MoveDocumentModal,
            label: this.__('documents.move'),
            icon: 'arrow-right',
            class: 'modal-sm',
            meta: {
              entityId: this.$route.params.entityId,
              documentFolders: response?.data?.data,
              document: {
                id: item.id,
                name: item.name,
              },
            },
            onSuccess: () => {
              this.$refs.documentsDataPage.refreshTable()
            },
          })
        }
      )
    },

    moveDocumentInBulk() {
      EntityDocumentService.listFolders(this.$route.params.entityId, {}).then(
        (response) => {
          this.showModal({
            component: MoveDocumentModal,
            label: this.tc(
              'documents.move-many',
              this.selectedDocuments.length
            ),
            icon: 'arrow-right',
            class: 'modal-sm',
            meta: {
              entityId: this.$route.params.entityId,
              documentFolders: response?.data?.data,
              document: this.selectedDocuments,
            },
            onSuccess: () => {
              this.$refs.documentsDataPage.refreshTable()
            },
          })
        }
      )
    },
  },
}
</script>
